import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { RootState } from '../redux/store';
import { tokenReceived, logout } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import { settings } from '../settings';
import {
    Riesgo,
    Examen,
    Establecimiento,
    Prestador,
    Localidad,
    AsignarExamenPayload,
    AsignarPrestadorPayload
} from '../pages/Periodicos/consts/periodicosTypes';
import { RECONFIRMATION_STATES_ENUM } from '../pages/Periodicos/consts/reconfirmationStates';
import { BatchesStatesEnum } from '../pages/Periodicos/components/BatchesStateBadge';
import { getBackdropUtilityClass } from '@mui/material';

export interface GetExposedEmployeesParams {
    cuil?: string;
    idEstablecimiento?: number;
    idOrganismoCentral?: number;
    nombre?: string;
    apellido?: string;
    estadoGeneral?: number;
    examen?: boolean;
    prestador?: boolean;
    idLocalidad?: number;
    idRiesgo?: number;
    idPrestador?: number;
    skip?: number;
    take?: number;
}

export interface GetReconfirmationEmployeesParams {
    idEmpleado?: number;
    cuil?: string;
    idEstablecimiento?: number;
    idOrganismoCentral?: number;
    nombre?: string;
    apellido?: string;
    estadoGeneral?: number;
    examen?: boolean;
    prestador?: boolean;
    idLocalidad?: number;
    idRiesgo?: number;
    idPrestador?: number;
    skip?: number;
    take?: number;
}

export interface ExportarPeriodicosPayload {
    cuil?: string;
    idEstablecimiento?: number;
    idOrganismoCentral?: number;
    nombre?: string;
    apellido?: string;
    estadoGeneral?: number;
    examen?: boolean;
    prestador?: boolean;
    idPrestador?: number | boolean;
    idLocalidad?: number;
    idRiesgo?: number;
}

export interface GetConvocatoriasParams {
    id: string;
    cuit: number;
    idOrganismoCentral: number;
    fecha: string;
    estado: number;
    skip: number;
    take: number;
}

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result = await baseQuery(args, api, extraOptions);
    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const periodicosApi = createApi({
    reducerPath: 'periodicosApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'ExposedEmployees',
        'ReconfirmationEmployees',
        'Batches',
        'BatchEmployees',
        'BatchExams'
    ],
    endpoints: (builder) => ({
        getExposedEmployees: builder.query<any, GetExposedEmployeesParams>({
            query: (params) => ({
                url: `periodicos/empleados-expuestos`,
                params
            }),
            providesTags: ['ExposedEmployees'],
            keepUnusedDataFor: 5
        }),
        getEmployeesPendingPrestador: builder.query<any, GetExposedEmployeesParams>({
            query: (params) => ({
                url: `periodicos/examenes-empleados`,
                params: {
                    ...params,
                    examen: true,
                    prestador: false
                }
            }),
            providesTags: ['ExposedEmployees'],
            keepUnusedDataFor: 5
        }),
        getEmployeesPendingConvocated: builder.query<any, GetExposedEmployeesParams>({
            query: (params) => ({
                url: `periodicos/examenes-empleados`,
                params: {
                    ...params,
                    examen: true,
                    prestador: true,
                    estadoGeneral: 2
                }
            }),
            providesTags: ['ExposedEmployees'],
            keepUnusedDataFor: 5
        }),
        getEmployeesConvocated: builder.query<any, GetExposedEmployeesParams>({
            query: (params) => ({
                url: `periodicos/empleados-convocatoria`,
                params: {
                    ...params
                }
            }),
            providesTags: ['ExposedEmployees'],
            keepUnusedDataFor: 5
        }),
        getExamenesEmpleado: builder.query<any, number>({
            query: (idEmpleado) => ({
                url: `periodicos/examenes-empleado/${idEmpleado}`
            }),
            transformResponse: (response: { data: any }) => response.data,
            providesTags: ['ExposedEmployees'],
            keepUnusedDataFor: 5
        }),
        getRiesgos: builder.query<Riesgo[], void>({
            query: () => `periodicos/riesgos`,
            transformResponse: (response: { data: Riesgo[] }) => response.data,
            providesTags: ['ExposedEmployees']
        }),

        //no se usa más
        getExamenesRiesgo: builder.query<Examen[], number>({
            query: (idRiesgo) => `periodicos/examenes-riesgos/${idRiesgo}`,
            transformResponse: (response: { data: Examen[] }) => response.data,
            providesTags: ['ExposedEmployees']
        }),

        getPrestadores: builder.query<
            Prestador[],
            { idProvincia?: number; idLocalidad?: number; tipo?: string }
        >({
            query: (params) => ({
                url: `periodicos/prestadores`,
                params
            }),
            transformResponse: (response: { data: Prestador[] }) => response.data,
            providesTags: ['ExposedEmployees']
        }),

        getLocalidadesPrestadores: builder.query<Localidad[], { idProvincia?: number }>({
            query: (params) => ({
                url: `periodicos/prestadores/localidades`,
                params
            }),
            transformResponse: (response: { data: { localidades: Localidad[] } }) =>
                response.data.localidades
        }),

        asignarExamenEmpleados: builder.mutation<
            any,
            { idExamen: number; payload: AsignarExamenPayload }
        >({
            query: ({ idExamen, payload }) => ({
                url: `periodicos/examen/${idExamen}/empleados`,
                method: 'POST',
                body: payload
            })
        }),
        //no se usa más
        asignarPrestadorExamenEmpleado: builder.mutation<
            any,
            { idExamenEmpleado: number; payload: AsignarPrestadorPayload }
        >({
            query: ({ idExamenEmpleado, payload }) => ({
                url: `periodicos/examen-empleado/${idExamenEmpleado}/prestador`,
                method: 'PATCH',
                body: payload
            })
        }),
        //no se usa más
        asignarPrestador: builder.mutation<any, { payload: AsignarPrestadorPayload }>({
            query: ({ payload }: { payload: AsignarPrestadorPayload }) => ({
                url: `periodicos/asignacion/prestador`,
                method: 'PATCH',
                body: payload
            }),
            invalidatesTags: ['ExposedEmployees']
        }),
        createDerivedBatch: builder.mutation<
            any,
            { idLoteOrigen: number; idMotivoDerivacion: number; idsEmpleadosLote: number[] }
        >({
            query: ({ idLoteOrigen, ...body }) => ({
                url: `periodicos/lotes/${idLoteOrigen}/derivados`,
                method: 'POST',
                body
            })
        }),
        updateEmployeeAttendance: builder.mutation<
            any,
            { cuilEmpleados: string[]; idAsistencia: number; estado: string }
        >({
            query: (payload) => ({
                url: `periodicos/empleados/asistencia`,
                method: 'PATCH',
                body: payload
            }),
            invalidatesTags: ['ExposedEmployees']
        }),
        generarConvocatorias: builder.mutation<any, { idsEmpleadosExamenes: number[] }>({
            query: (payload) => ({
                url: 'periodicos/convocatoria',
                method: 'POST',
                body: payload
            }),
            invalidatesTags: ['ExposedEmployees']
        }),
        asignarTurnoExamenEmpleados: builder.mutation<
            any,
            { idExamenEmpleados: number[]; fechaTurno: string }
        >({
            query: (payload) => ({
                url: `periodicos/examen-empleados/turno`,
                method: 'PATCH',
                body: payload
            }),
            invalidatesTags: ['ExposedEmployees']
        }),
        assignBatchEmployeesShift: builder.mutation<
            any,
            { batchId: number; idsEmpleadosLote: number[]; fechaTurno: string }
        >({
            query: ({ batchId, ...body }) => ({
                url: `periodicos/lotes/${batchId}/asignar-turno`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['Batches']
        }),
        markBatchEmployeeAttendance: builder.mutation<
            any,
            { idsEmpleadosLote: number[]; asistencia: number }
        >({
            query: (body) => ({
                url: `periodicos/lotes/empleados/marcar-asistencia`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: ['Batches', 'ExposedEmployees']
        }),
        getAptitudes: builder.query<any[], void>({
            query: () => 'periodicos/aptitudes',
            transformResponse: (response: { data: { aptitudes: any[] } }) => response.data.aptitudes
        }),

        asignarResultadoExamenEmpleado: builder.mutation<
            any,
            {
                idExamenEmpleado: number;
                payload: {
                    idAptitud?: number;
                    fechaResultado: string;
                    observaciones: string;
                    archivoResultado?: File | null;
                };
            }
        >({
            query: ({ idExamenEmpleado, payload }) => {
                const formData = new FormData();
                if (payload.idAptitud != null) {
                    formData.append('idAptitud', payload.idAptitud.toString());
                }
                formData.append('fechaResultado', payload.fechaResultado);
                formData.append('observaciones', payload.observaciones);
                if (payload.archivoResultado) {
                    formData.append('file', payload.archivoResultado);
                }

                return {
                    url: `periodicos/examen-empleado/${idExamenEmpleado}/resultado`,
                    method: 'PATCH',
                    body: formData
                };
            },
            invalidatesTags: ['ExposedEmployees']
        }),

        exportarPeriodicos: builder.mutation<
            { type: string; data: number[] },
            ExportarPeriodicosPayload
        >({
            query: (payload) => ({
                url: `periodicos/exportar`,
                method: 'POST',
                body: payload,
                responseHandler: (response) => response.json()
            })
        }),
        generarPdfResultadosConvocatoria: builder.mutation<{ data: string }, number>({
            query: (idConvocatoria) => ({
                url: `periodicos/convocatoria/${idConvocatoria}/pdf/resultados`,
                method: 'POST'
            })
        }),

        generarPdfConvocatoria: builder.mutation<{ data: string }, number>({
            query: (idConvocatoria) => ({
                url: `periodicos/convocatoria/${idConvocatoria}/pdf/convocatoria`,
                method: 'POST'
            })
        }),

        generarPdfCertificadoAptitud: builder.mutation<{ data: string }, number>({
            query: (idExamenEmpleado) => ({
                url: `periodicos/examen-empleado/${idExamenEmpleado}/pdf`,
                method: 'POST'
            })
        }),

        eliminarExamenEmpleado: builder.mutation<any, number>({
            query: (idExamenEmpleado) => ({
                url: `periodicos/examen-empleado/${idExamenEmpleado}`,
                method: 'DELETE'
            })
        }),
        getConvocatorias: builder.query<any, GetConvocatoriasParams>({
            query: (params) => ({
                url: `periodicos/convocatoria`,
                params
            }),
            keepUnusedDataFor: 5
        }),
        getEstablecimientosPeriodicos: builder.query<Establecimiento[], void>({
            query: () => ({
                url: 'periodicos/establecimientos'
            }),
            transformResponse: (response: { data: { establecimientos: Establecimiento[] } }) =>
                response.data.establecimientos
        }),
        getReconfirmationEmployees: builder.query<any, GetReconfirmationEmployeesParams>({
            query: (params) => ({
                url: `periodicos/empleados-expuestos-reconfirmacion`,
                params: { ...params, estado: RECONFIRMATION_STATES_ENUM.PENDIENTE_EXAMEN }
            }),
            providesTags: ['ReconfirmationEmployees'],
            keepUnusedDataFor: 5
        }),
        getReconfirmationEmployeesPendingConvocated: builder.query<
            any,
            GetReconfirmationEmployeesParams
        >({
            query: (params) => ({
                url: `periodicos/empleados-expuestos-reconfirmacion`,
                params: { ...params, estado: RECONFIRMATION_STATES_ENUM.PENDIENTE_CONVOCATORIA }
            }),
            providesTags: ['ReconfirmationEmployees'],
            keepUnusedDataFor: 5
        }),
        getReconfirmationEmployeesConvocated: builder.query<any, GetReconfirmationEmployeesParams>({
            query: (params) => ({
                url: `periodicos/empleados-expuestos-reconfirmacion`,
                params: { ...params, estado: RECONFIRMATION_STATES_ENUM.CONVOCADO }
            }),
            providesTags: ['ReconfirmationEmployees'],
            keepUnusedDataFor: 5
        }),
        getReconfirmationEmployeesPendingPrestador: builder.query<
            any,
            GetReconfirmationEmployeesParams
        >({
            query: (params) => ({
                url: `periodicos/empleados-expuestos-reconfirmacion`,
                params: { ...params, estado: RECONFIRMATION_STATES_ENUM.PENDIENTE_PRESTADOR }
            }),
            providesTags: ['ReconfirmationEmployees'],
            keepUnusedDataFor: 5
        }),
        assignReconfirmationEmployeeExam: builder.mutation<
            { data: string },
            {
                idEmpleado: number;
                body: { nombre: string };
            }
        >({
            query: ({ idEmpleado, body }) => ({
                url: `periodicos/empleados-expuestos-reconfirmacion/${idEmpleado}/examenes`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['ReconfirmationEmployees']
        }),
        getEmployeesResults: builder.query<any, GetReconfirmationEmployeesParams>({
            query: (params) => ({
                url: `periodicos/empleados-expuestos-reconfirmacion`,
                params: { ...params, estado: RECONFIRMATION_STATES_ENUM.RESULTADOS }
            }),
            providesTags: ['ReconfirmationEmployees'],
            keepUnusedDataFor: 5
        }),
        getPdfResultadosExamenEmpleado: builder.query<{ data: string } | Blob, number>({
            query: (idExamenEmpleado) => ({
                url: `periodicos/examen-empleado/${idExamenEmpleado}/resultados`,
                method: 'GET'
            })
        }),
        getPeriodicosQuantity: builder.query<any, any>({
            query: (establishmentId) => ({
                url: `periodicos/cantidades-examenes-periodicos/${establishmentId}`
            })
        }),
        getBatchesPendientes: builder.query<any, any>({
            query: (params) => ({
                url: `periodicos/lotes`,
                params: { ...params, idEstadoLote: BatchesStatesEnum.PENDIENTE }
            }),
            providesTags: ['Batches']
        }),
        getBatchesEnCurso: builder.query<any, any>({
            query: (params) => ({
                url: `periodicos/lotes`,
                params: { ...params, idEstadoLote: BatchesStatesEnum.EN_CURSO }
            }),
            providesTags: ['Batches']
        }),
        getBatchesCerrados: builder.query<any, any>({
            query: (params) => ({
                url: `periodicos/lotes`,
                params: { ...params, idEstadoLote: BatchesStatesEnum.CERRADO }
            }),
            providesTags: ['Batches']
        }),
        getBatchHistory: builder.query<any, number>({
            query: (batchId) => ({
                url: `periodicos/lotes/${batchId}/historial`
            })
        }),
        // Provisional endpoint while backend contract for employee-level history is finalized.
        getBatchEmployeeHistoryProvisional: builder.query<
            any,
            { batchId: number; employeeId: number }
        >({
            query: ({ batchId, employeeId }) => ({
                url: `periodicos/lotes/${batchId}/empleados/${employeeId}/historial`
            })
        }),
        // Provisional endpoint while backend contract for medical audit detail is finalized.
        getBatchEmployeeMedicalAuditProvisional: builder.query<
            any,
            { batchId: number; employeeId: number }
        >({
            query: ({ batchId, employeeId }) => ({
                url: `periodicos/lotes/${batchId}/empleados/${employeeId}/auditoria-medica`
            })
        }),
        // Provisional endpoint to download employee result file from batch detail row action.
        downloadBatchEmployeeResultsProvisional: builder.mutation<
            { data?: string },
            { batchId: number; employeeId: number }
        >({
            query: ({ batchId, employeeId }) => ({
                url: `periodicos/lotes/${batchId}/empleados/${employeeId}/resultados/pdf`,
                method: 'GET'
            })
        }),
        getBatchDetail: builder.query<any, { batchId: number; params?: any }>({
            query: ({ batchId, params }) => ({
                url: `periodicos/lotes/${batchId}/empleados`,
                params: { ...params }
            }),
            providesTags: ['BatchEmployees']
        }),
        assignPrestador: builder.mutation<any, { idPrestador: number; idLotes: number[] }>({
            query: (payload) => ({
                url: `periodicos/lotes/asignar-prestador`,
                method: 'POST',
                body: payload
            }),
            invalidatesTags: ['Batches']
        }),
        getBatchEmployeeExams: builder.query<any[], number>({
            query: (idEmpleadoLote) => ({
                url: `periodicos/examenes/${idEmpleadoLote}`
            }),
            transformResponse: (response: { data: any[] }) => response.data,
            providesTags: ['BatchExams'],
            keepUnusedDataFor: 5
        }),
        getExamenDocumento: builder.query<{ data: string }, number>({
            query: (idExamen) => ({
                url: `periodicos/examenes/${idExamen}/documento`
            })
        }),
        getBatchEmployeeDocuments: builder.query<any, number>({
            query: (idEmpleadoLote) => ({
                url: `periodicos/examenes/${idEmpleadoLote}/documentos`
            }),
            providesTags: ['Batches']
        }),
        uploadBatchEmployeeDocuments: builder.mutation<
            any,
            {
                idEmpleado: number;
                files: { file: File; idExamenEmpleado: number; examen: string }[];
            }
        >({
            query: ({ idEmpleado, files }) => {
                const formData = new FormData();
                files.forEach(({ file, examen }) => {
                    const ext = file.name.split('.').pop();
                    formData.append('documentos', file, ext ? `${examen}.${ext}` : examen);
                });
                formData.append(
                    'payload',
                    JSON.stringify({
                        examenes: files.map(({ idExamenEmpleado, examen, file }) => {
                            const ext = file.name.split('.').pop();
                            return {
                                idExamenEmpleado,
                                nombreExamen: ext ? `${examen}.${ext}` : examen
                            };
                        })
                    })
                );
                return {
                    url: `periodicos/lotes/${idEmpleado}/cargar-documentos-examenes`,
                    method: 'POST',
                    body: formData
                };
            },
            invalidatesTags: (_, error) => (error ? [] : ['BatchEmployees'])
        }),
        uploadBatchEmployeeDocumentsSimple: builder.mutation<
            any,
            { idEmpleado: number; files: File[] }
        >({
            query: ({ idEmpleado, files }) => {
                const formData = new FormData();
                files.forEach((file) => formData.append('documentos', file));
                return {
                    url: `periodicos/lotes/${idEmpleado}/cargar-documentos-examenes`,
                    method: 'POST',
                    body: formData
                };
            },
            invalidatesTags: (_, error) => (error ? [] : ['BatchEmployees'])
        }),
        loadBatchEmployeeResults: builder.mutation<
            any,
            {
                idEmpleado: number;
                resultados: {
                    idExamenEmpleado: number;
                    idAptitud: number;
                    observaciones?: string;
                }[];
            }
        >({
            query: ({ idEmpleado, resultados }) => ({
                url: `periodicos/lotes/${idEmpleado}/cargar-resultados`,
                method: 'PATCH',
                body: { resultados }
            }),
            invalidatesTags: (_, error) => (error ? [] : ['BatchEmployees', 'BatchExams'])
        }),
        closeBatch: builder.mutation<any, { batchId: number }>({
            query: ({ batchId }) => ({
                url: `periodicos/lotes/${batchId}/cerrar-lote`,
                method: 'POST'
            }),
            invalidatesTags: ['Batches']
        }),
        getReconfirmationSelectionEmployees: builder.query<any, number>({
            query: (batchId) => ({
                url: `periodicos/lotes/${batchId}/reconfirmacion/seleccion-empleados`
            }),
            providesTags: ['Batches'],
            keepUnusedDataFor: 5
        }),
        createReconfirmationBatch: builder.mutation<
            any,
            { batchId: number; idsEmpleadosLote: number[] }
        >({
            query: ({ batchId, idsEmpleadosLote }) => ({
                url: `periodicos/lotes/${batchId}/reconfirmacion`,
                method: 'POST',
                body: { idsEmpleadosLote }
            }),
            invalidatesTags: ['Batches']
        }),
        // Provisional endpoint — confirm URL with backend before shipping.
        deleteBatchEmployeeDocument: builder.mutation<
            any,
            { idEmpleadoLote: number; idDocumento: number }
        >({
            query: ({ idEmpleadoLote, idDocumento }) => ({
                url: `periodicos/examenes/${idEmpleadoLote}/documentos`,
                method: 'DELETE',
                body: { idsDocumento: [idDocumento] }
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Batches'])
        })
    })
});

export const {
    useGetExposedEmployeesQuery,
    useGetEmployeesPendingPrestadorQuery,
    useGetEmployeesPendingConvocatedQuery,
    useGetEmployeesConvocatedQuery,
    useLazyGetEmployeesPendingPrestadorQuery,
    useLazyGetEmployeesPendingConvocatedQuery,
    useLazyGetEmployeesConvocatedQuery,
    useLazyGetExposedEmployeesQuery,
    useGetRiesgosQuery,
    useGetLocalidadesPrestadoresQuery,
    useLazyGetLocalidadesPrestadoresQuery,
    useGetExamenesRiesgoQuery,
    useAsignarExamenEmpleadosMutation,
    useGetPrestadoresQuery,
    useLazyGetPrestadoresQuery,
    useAsignarPrestadorMutation,
    useCreateDerivedBatchMutation,
    useUpdateEmployeeAttendanceMutation,
    useGenerarConvocatoriasMutation,
    useAsignarTurnoExamenEmpleadosMutation,
    useAssignBatchEmployeesShiftMutation,
    useMarkBatchEmployeeAttendanceMutation,
    useGetAptitudesQuery,
    useAsignarResultadoExamenEmpleadoMutation,
    useExportarPeriodicosMutation,
    useGenerarPdfResultadosConvocatoriaMutation,
    useGenerarPdfConvocatoriaMutation,
    useGetConvocatoriasQuery,
    useLazyGetConvocatoriasQuery,
    useGetEstablecimientosPeriodicosQuery,
    useLazyGetEstablecimientosPeriodicosQuery,
    useLazyGetReconfirmationEmployeesQuery,
    useLazyGetReconfirmationEmployeesPendingPrestadorQuery,
    useLazyGetReconfirmationEmployeesPendingConvocatedQuery,
    useLazyGetReconfirmationEmployeesConvocatedQuery,
    useAssignReconfirmationEmployeeExamMutation,
    useLazyGetEmployeesResultsQuery,
    useGetExamenesEmpleadoQuery,
    useLazyGetExamenesEmpleadoQuery,
    useGetPdfResultadosExamenEmpleadoQuery,
    useLazyGetPdfResultadosExamenEmpleadoQuery,
    useGenerarPdfCertificadoAptitudMutation,
    useGetPeriodicosQuantityQuery,
    useLazyGetBatchesPendientesQuery,
    useLazyGetBatchesEnCursoQuery,
    useLazyGetBatchesCerradosQuery,
    useGetBatchHistoryQuery,
    useGetBatchEmployeeHistoryProvisionalQuery,
    useGetBatchEmployeeMedicalAuditProvisionalQuery,
    useDownloadBatchEmployeeResultsProvisionalMutation,
    useGetBatchDetailQuery,
    useLazyGetBatchDetailQuery,
    useAssignPrestadorMutation,
    useGetBatchEmployeeExamsQuery,
    useLazyGetBatchEmployeeExamsQuery,
    useGetExamenDocumentoQuery,
    useLazyGetExamenDocumentoQuery,
    useGetBatchEmployeeDocumentsQuery,
    useLazyGetBatchEmployeeDocumentsQuery,
    useUploadBatchEmployeeDocumentsMutation,
    useUploadBatchEmployeeDocumentsSimpleMutation,
    useLoadBatchEmployeeResultsMutation,
    useCloseBatchMutation,
    useGetReconfirmationSelectionEmployeesQuery,
    useLazyGetReconfirmationSelectionEmployeesQuery,
    useCreateReconfirmationBatchMutation,
    useDeleteBatchEmployeeDocumentMutation,
} = periodicosApi;
